import { NgTemplateOutlet } from "@angular/common";
import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  input,
  output,
  signal,
  TemplateRef,
  viewChildren,
} from "@angular/core";
import { MatRippleModule } from "@angular/material/core";
import { MatIconModule } from "@angular/material/icon";
import {
  MenuComponent,
  MenuItemSelectEvent,
  MenuTriggerDirective,
  EnrTooltipDirective,
} from "@corp-ais/omniark-ui";
import { MenuIcon } from "@app/shared/components/menu-icon/menu-icon";
import {
  ISideNavItem,
  ISideNavItemSelectEvent,
  SideNavCollapsedInteractionType,
} from "./side-nav.types";
import { buildMenuItems } from "./side-nav.mapper";
import {
  filterItems,
  getItemById,
  getItemPath,
  getItemPathIds,
  getLeafOptions,
  hasChildren,
  hasItemIcons,
} from "./side-nav.utils";

@Component({
  selector: "app-side-nav",
  imports: [
    MatIconModule,
    MatRippleModule,
    MenuComponent,
    MenuTriggerDirective,
    EnrTooltipDirective,
    MenuIcon,
    NgTemplateOutlet,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: "./side-nav.html",
  styleUrl: "./side-nav.scss",
  host: {
    class: "side-nav-host side-nav-host--frameless",
    "[class.side-nav-host--collapsed]": "railCollapsed()",
  },
})
export class SideNav {
  readonly items = input<ISideNavItem[]>([]);
  readonly pinnedItems = input<ISideNavItem[]>([]);

  readonly activeItemId = input<string | null>(null);
  readonly activePath = input<string | null>(null);

  readonly title = input("");
  readonly ariaLabel = input("Main menu");
  readonly emptyTitle = input("No menu found");
  readonly emptyHint = input("Try another keyword.");
  readonly searchQuery = input("");
  readonly accordion = input(false);
  readonly allowCollapseActiveAncestors = input(true);
  readonly expandTopLevelByDefault = input(true);
  readonly collapsed = input(false);
  readonly collapsedInteraction =
    input<SideNavCollapsedInteractionType>("menu");
  readonly expandedWidth = input("220px");
  readonly collapsedWidth = input("64px");
  readonly iconSize = input("18px");
  readonly iconTemplate = input<TemplateRef<{
    $implicit: ISideNavItem;
    icon: string;
    collapsed: boolean;
  }> | null>(null);

  readonly itemSelect = output<ISideNavItemSelectEvent>();
  readonly collapsedChange = output<boolean>();
  readonly groupExpandedChange = output<{
    itemId: string;
    expanded: boolean;
  }>();

  readonly hasChildren = hasChildren;
  readonly hasItemIcons = hasItemIcons;
  readonly buildMenuItems = buildMenuItems;

  readonly railCollapsed = signal(false);
  readonly isWidthAnimating = signal(false);
  private readonly expandedItemIds = signal<ReadonlySet<string>>(new Set());
  private readonly userCollapsedItemIds = signal<ReadonlySet<string>>(
    new Set(),
  );
  private readonly collapsedMenus =
    viewChildren<MenuComponent>("collapsedMenu");
  private widthAnimationTimer: ReturnType<typeof setTimeout> | null = null;
  private lastSeededItemKey = "";
  private lastSyncedCollapsed: boolean | null = null;
  private lastClosedMenusActiveKey: string | null = null;
  private lastExpandedActiveId: string | null = null;
  readonly effectiveItems = computed(() => this.items());

  readonly filteredItems = computed(() => {
    const query = this.searchQuery().trim().toLocaleLowerCase();
    if (!query) return this.effectiveItems();
    return filterItems(this.effectiveItems(), query);
  });

  readonly compactLeafOptions = computed(() =>
    getLeafOptions(this.filteredItems()),
  );

  constructor() {
    effect(() => {
      const items = this.effectiveItems();
      const itemKey = items.map((item) => item.id).join("\0");
      const activeId = this.getActiveId();
      const ancestors = activeId
        ? getItemPathIds(items, activeId).slice(0, -1)
        : [];
      const allowCollapse = this.allowCollapseActiveAncestors();
      const activeChanged = activeId !== this.lastExpandedActiveId;
      if (activeChanged) {
        this.lastExpandedActiveId = activeId;
      }

      if (allowCollapse && activeChanged && ancestors.length) {
        this.userCollapsedItemIds.update((current) => {
          if (!ancestors.some((id) => current.has(id))) return current;
          const next = new Set(current);
          for (const id of ancestors) next.delete(id);
          return next;
        });
      }

      const userCollapsed = this.userCollapsedItemIds();
      this.expandedItemIds.update((current) => {
        if (this.accordion() && !allowCollapse) {
          return new Set(ancestors);
        }

        const next = this.accordion() ? new Set<string>() : new Set(current);
        if (
          !this.accordion() &&
          this.expandTopLevelByDefault() &&
          itemKey !== this.lastSeededItemKey
        ) {
          this.lastSeededItemKey = itemKey;
          for (const item of items) {
            if (hasChildren(item)) next.add(item.id);
          }
        }

        for (const id of ancestors) {
          if (allowCollapse && !activeChanged && userCollapsed.has(id)) {
            continue;
          }
          next.add(id);
        }
        return next;
      });
    });
    effect(() => {
      const next = this.collapsed();
      if (this.lastSyncedCollapsed === next) return;
      this.lastSyncedCollapsed = next;
      this.closeAllMenus();
      if (this.railCollapsed() === next) return;
      this.beginWidthAnimation();
      this.railCollapsed.set(next);
    });
    effect(() => {
      const activeKey = this.activeItemId() ?? this.activePath() ?? "";
      if (this.lastClosedMenusActiveKey === null) {
        this.lastClosedMenusActiveKey = activeKey;
        return;
      }
      if (this.lastClosedMenusActiveKey === activeKey) return;
      this.lastClosedMenusActiveKey = activeKey;
      this.closeAllMenus();
    });
  }

  shouldOpenCollapsedMenu(item: ISideNavItem): boolean {
    return getLeafOptions([item]).length > 1;
  }

  onCollapsedSoleLeafActivate(
    item: ISideNavItem,
    ancestors: ISideNavItem[],
  ): void {
    if (item.disabled) return;
    this.closeAllMenus();
    const soleLeaf = getLeafOptions([item], ancestors)[0];
    if (!soleLeaf || soleLeaf.item.disabled) return;
    this.emitSelection(soleLeaf.item, soleLeaf.ancestors);
  }

  isItemExpanded(itemId: string): boolean {
    if (this.searchQuery().trim()) {
      return getItemById(this.filteredItems(), itemId) !== null;
    }
    return this.expandedItemIds().has(itemId);
  }

  isItemActive(item: ISideNavItem): boolean {
    const activeItemId = this.activeItemId();
    if (activeItemId && item.id === activeItemId) return true;

    const activePath = this.activePath();
    if (activePath && item.path) {
      if (activePath === item.path) return true;
      if (activePath.startsWith(`${item.path}/`)) return true;
    }

    return false;
  }

  isBranchActive(item: ISideNavItem): boolean {
    if (this.isItemActive(item)) return true;
    return (item.children ?? []).some((child) => this.isBranchActive(child));
  }

  toggleItem(item: ISideNavItem): void {
    if (!hasChildren(item)) return;
    const expanded = !this.expandedItemIds().has(item.id);
    this.expandedItemIds.update((current) => {
      if (this.accordion()) {
        return expanded ? new Set([item.id]) : new Set();
      }
      const next = new Set(current);
      if (expanded) next.add(item.id);
      else next.delete(item.id);
      return next;
    });
    if (this.allowCollapseActiveAncestors()) {
      this.userCollapsedItemIds.update((current) => {
        const next = new Set(current);
        if (expanded) next.delete(item.id);
        else next.add(item.id);
        return next;
      });
    }
    this.groupExpandedChange.emit({ itemId: item.id, expanded });
  }

  onItemActivate(item: ISideNavItem, ancestors: ISideNavItem[]): void {
    if (item.disabled) return;

    if (this.railCollapsed()) {
      if (this.collapsedInteraction() === "expand") {
        this.toggleRailCollapsed();
        return;
      }
    }

    if (hasChildren(item)) {
      this.toggleItem(item);
      return;
    }

    this.emitSelection(item, ancestors);
  }

  toggleRailCollapsed(): void {
    const next = !this.railCollapsed();
    this.lastSyncedCollapsed = next;
    this.closeAllMenus();
    this.beginWidthAnimation();
    this.railCollapsed.set(next);
    this.collapsedChange.emit(next);
  }

  onCompactChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    const option = this.compactLeafOptions().find(
      (entry) => entry.item.id === value,
    );
    if (option) this.emitSelection(option.item, option.ancestors);
  }

  onCollapsedMenuActivate(menu: MenuComponent): void {
    for (const other of this.collapsedMenus()) {
      if (other !== menu) {
        other.close();
      }
    }
  }

  onCollapsedMenuSelect(
    rootItem: ISideNavItem,
    event: MenuItemSelectEvent,
  ): void {
    const path = getItemPath([rootItem], event.item.id);
    if (path.length < 2) return;
    const item = path[path.length - 1]!;
    if (item.disabled) return;
    this.emitSelection(item, path.slice(0, -1));
  }

  private closeAllMenus(): void {
    for (const menu of this.collapsedMenus()) {
      menu.close();
    }
  }

  private emitSelection(item: ISideNavItem, ancestors: ISideNavItem[]): void {
    this.itemSelect.emit({ item, ancestors });
  }

  private getActiveId(): string | null {
    return this.activeItemId() ?? this.activePath();
  }

  private beginWidthAnimation(): void {
    this.isWidthAnimating.set(true);
    if (this.widthAnimationTimer !== null) {
      clearTimeout(this.widthAnimationTimer);
    }
    this.widthAnimationTimer = setTimeout(() => {
      this.isWidthAnimating.set(false);
      this.widthAnimationTimer = null;
    }, 360);
  }
}
